/**
 * Benchmark Insights Page (F052)
 *
 * Route: /policy-intelligence/benchmarks | Nav: Enforcement > Benchmark Insights
 *
 * Shows how the tenant's governance posture compares to anonymised peer
 * benchmarks across the Policy Intelligence Network. Metrics include block
 * rate, exception rate, FP rate, evidence rate, and remediation rate per
 * policy family with peer median, interquartile range, and percentile
 * positioning.
 *
 * Peer comparison is k-anonymity gated server-side — a metric with an
 * insufficient peer cohort comes back without a comparable peer value. This
 * page never substitutes a fabricated peer value for a suppressed one; every
 * metric renders through <BenchmarkCard>, which is the single place that
 * decides whether peer data is safe to show.
 */
import React, { useMemo, useState } from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  Dropdown,
  Option,
  Spinner,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { useBenchmarkComparison } from '../hooks/usePolicyIntelligence';
import { BenchmarkCard, type BenchmarkData } from '../components/verdict/BenchmarkCard';
import type { BenchmarkMetric } from '../services/policyIntelligenceApi';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  scopeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  benchmarkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
  summaryCard: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    padding: tokens.spacingHorizontalL,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    alignItems: 'center',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  suppressionNote: {
    color: tokens.colorNeutralForeground3,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatFamilyName = (family: string): string =>
  family.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const peerScopeOptions = [
  { value: 'global', label: 'Global' },
  { value: 'same_industry', label: 'Same Industry' },
  { value: 'same_size_band', label: 'Same Size Band' },
];

/**
 * Direction is derived from the API's own classification + values — never a
 * fabricated per-metric-name business rule. "stronger_than_peers" means the
 * tenant is doing better than peers on this metric; combined with whether the
 * tenant sits above or below the peer median, that tells us which direction
 * is favourable for this specific metric.
 */
function directionIsHigherBetter(metric: BenchmarkMetric): boolean {
  if (metric.peerMedian === undefined || metric.peerMedian === null) {
    return true; // irrelevant — BenchmarkCard won't render a comparison without peer data anyway
  }
  const tenantAboveMedian = metric.tenantValue >= metric.peerMedian;
  const isStronger = metric.classification === 'stronger_than_peers';
  return isStronger ? tenantAboveMedian : !tenantAboveMedian;
}

function toBenchmarkData(metric: BenchmarkMetric): BenchmarkData {
  return {
    metricLabel: `${formatFamilyName(metric.policyFamily)} — ${metric.metricName}`,
    tenantValue: metric.tenantValue,
    peerValue: metric.suppressed ? undefined : (metric.peerMedian ?? undefined),
    sampleSize: metric.suppressed ? undefined : metric.sampleSize,
    cohortDescription: metric.suppressed ? undefined : metric.cohortDescription,
    higherIsBetter: directionIsHigherBetter(metric),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BenchmarkInsightsPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const [scope, setScope] = useState('global');

  const { data, isLoading, error } = useBenchmarkComparison(scope);
  const benchmarks = data?.metrics ?? [];

  const summary = useMemo(() => {
    const stronger = benchmarks.filter((b) => b.classification === 'stronger_than_peers').length;
    const weaker = benchmarks.filter((b) => b.classification === 'weaker_than_peers').length;
    const underCovered = benchmarks.filter((b) => b.classification === 'materially_under_covered').length;
    return { stronger, weaker, underCovered };
  }, [benchmarks]);

  const suppressedCount = useMemo(
    () => benchmarks.filter((b) => b.suppressed || b.peerMedian === undefined || b.peerMedian === null).length,
    [benchmarks]
  );

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Benchmark Insights"
          subtitle="How your governance posture compares to peers"
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner size="large" label="Loading benchmark data..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Benchmark Insights"
          subtitle="How your governance posture compares to peers"
        />
        <Body1 style={{ color: tokens.colorPaletteRedForeground1, textAlign: 'center', padding: tokens.spacingVerticalXXL }}>
          Failed to load benchmark data. Please try again later.
        </Body1>
      </div>
    );
  }

  if (benchmarks.length === 0) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Benchmark Insights"
          subtitle="How your governance posture compares to peers"
        />
        <Card>
          <div className={styles.emptyContainer}>
            <Title3>Benchmark data not available</Title3>
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              Enable network participation in Policy Intelligence Settings to see peer comparisons.
            </Body1>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Benchmark Insights"
        subtitle="How your governance posture compares to peers"
      />

      {/* Peer scope selector */}
      <div className={styles.scopeRow}>
        <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Peer scope:</Body2>
        <Dropdown
          value={peerScopeOptions.find((o) => o.value === scope)?.label ?? 'Global'}
          selectedOptions={[scope]}
          onOptionSelect={(_, data) => {
            if (data.optionValue) setScope(data.optionValue);
          }}
        >
          {peerScopeOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* Summary card */}
      <Card>
        <div className={styles.summaryCard}>
          <Body1 style={{ fontWeight: 600 }}>Summary:</Body1>
          <div className={styles.summaryItem}>
            <Title2 style={{ color: tokens.colorPaletteGreenForeground1 }}>{summary.stronger}</Title2>
            <Caption1>Stronger than peers</Caption1>
          </div>
          <div className={styles.summaryItem}>
            <Title2 style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}>{summary.weaker}</Title2>
            <Caption1>Weaker than peers</Caption1>
          </div>
          <div className={styles.summaryItem}>
            <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>{summary.underCovered}</Title2>
            <Caption1>Materially under-covered</Caption1>
          </div>
          {suppressedCount > 0 && (
            <div className={styles.summaryItem}>
              <Title2 style={{ color: tokens.colorNeutralForeground3 }}>{suppressedCount}</Title2>
              <Caption1>Peer data not available</Caption1>
            </div>
          )}
        </div>
      </Card>

      {suppressedCount > 0 && (
        <Caption1 className={styles.suppressionNote}>
          {suppressedCount === benchmarks.length
            ? 'Peer comparisons are not available for any metric right now — the anonymised peer cohort is too small to compare safely.'
            : `Peer comparisons are not available for ${suppressedCount} of ${benchmarks.length} metrics — the anonymised peer cohort is too small to compare safely for those metrics.`}
        </Caption1>
      )}

      {/* Benchmark metric cards grid — routed entirely through BenchmarkCard, which decides
          whether peer data is safe to render. */}
      <div className={styles.benchmarkGrid}>
        {benchmarks.map((metric) => (
          <BenchmarkCard
            key={`${metric.policyFamily}-${metric.metricName}`}
            data={toBenchmarkData(metric)}
          />
        ))}
      </div>
    </div>
  );
};
